// src/pages/EndToEndEnterprise.tsx

import { useEffect } from "react";
import { useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  ArrowLeft,
  CheckCircle,
  Target,
  Rocket,
  Workflow,
  ShieldCheck,
} from "lucide-react";

import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import GetStartedDialog from "@/components/GetStartedDialog";
import consultingImage from "@/assets/consulting-roadmap.jpg";
import implementationImage from "@/assets/implementation-migration.jpg";
import integrationImage from "@/assets/integration-customization.jpg";
import managedServicesImage from "@/assets/managed-services.jpg";

// Optional fallback:
import defaultEnterpriseImage from "@/assets/sap-end-to-end-solutions-clean.png";

const EndToEndEnterprise = () => {
  const { serviceId } = useParams();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [serviceId]);

  const enterpriseSolutions: Record<string, any> = {
    "c&rs": {
      icon: Target,
      badge: "Consulting & Strategy",
      title: "Consulting & Roadmap Strategy",
      heroTitle: "Strategic Consulting & Enterprise Roadmap Planning",
      heroSubtitle:
        "Define a clear digital transformation roadmap aligned with business goals.",
      subtitle:
        "Define a clear digital transformation roadmap aligned with business goals.",
      image: consultingImage,
      imageTitle: "Strategic Advisory & Executive Planning",
      imageSubtitle:
        "Collaborative consulting sessions, roadmap design, and enterprise transformation workshops.",
      description:
        "We align your technology vision with business priorities through advisory, architecture planning, and long-term roadmap creation...",
      keyServices: [
        "Digital transformation assessments & maturity modelling",
        "Technology selection, vendor analysis, and proof-of-concept design",
        "Executive stakeholder workshops and alignment sessions",
        "3–5 year IT roadmap planning with phased milestones",
        "Business case development and ROI modelling",
        "IT Infrastructure Assessment",
        "Enterprise Architecture Planning",
        "Cost Optimization & ROI Planning",
      ],
      engagement: {
        type: "Advisory, Workshop, or Full Roadmap Program",
        duration: "4 weeks – 6 months depending on scope",
        deliverables:
          "Executive roadmap, technology assessment report, vendor shortlist",
        team: "Senior consultant, solution architect, industry SME",
        outcome: "Clear, prioritized transformation plan with executive buy-in",
      },
    },

    "i&m": {
      icon: Rocket,
      badge: "Implementation & Migration",
      title: "Implementation & Migration",
      heroTitle: "Enterprise Deployment, Migration & Modernization",
      heroSubtitle:
        "Deploy, modernize, and migrate enterprise systems with minimal disruption.",
      subtitle:
        "Deploy, modernize, and migrate enterprise systems with minimal disruption.",
      image: implementationImage,
      imageTitle: "Deployment & Migration Execution",
      imageSubtitle:
        "Enterprise system modernization, cloud adoption, and seamless deployment.",
      description:
        "We manage full lifecycle deployments for ERP, CRM, cloud, and infrastructure transformation...",
      keyServices: [
        "Cloud migration to AWS, Azure, and Google Cloud",
        "ERP/CRM implementations (SAP, Salesforce, Oracle, Dynamics)",
        "Legacy system modernization",
        "Data migration & validation",
        "Zero-downtime cutover planning",
        "Infrastructure deployment",
        "Application upgrade & rollout",
        "Post-migration hypercare and stabilization",
      ],
      engagement: {
        type: "Fixed-scope project or agile delivery program",
        duration: "3 months – 2 years depending on scope",
        deliverables:
          "Migrated systems, runbooks, training materials, go-live report",
        team: "Project manager, cloud architects, data engineers, testers",
        outcome: "Fully migrated, validated, and production-ready environment",
      },
    },

    "i&c": {
      icon: Workflow,
      badge: "Integration & Customization",
      title: "Integration & Customization",
      heroTitle: "System Integration, Automation & Custom Engineering",
      heroSubtitle:
        "Connect systems and build custom enterprise workflows for maximum efficiency.",
      subtitle:
        "Connect systems and build custom enterprise workflows for maximum efficiency.",
      image: integrationImage,
      imageTitle: "Unified Integration Ecosystem",
      imageSubtitle:
        "APIs, automation, middleware, and connected enterprise engineering.",
      description:
        "We create resilient integration ecosystems that unify platforms...",
      keyServices: [
        "API design, development, and governance (REST, GraphQL, gRPC)",
        "Third-party system integration",
        "Custom software development",
        "Workflow automation",
        "Dashboard & reporting solutions",
        "Legacy system modernization",
        "Real-time data streaming pipelines",
        "Mobile & web application customization",
      ],
      engagement: {
        type: "Project-based development or retainer model",
        duration: "6 weeks – 12 months",
        deliverables:
          "Deployed integrations, API documentation, architecture diagrams",
        team: "Solution architect, backend developers, QA engineers",
        outcome: "Unified, automated workflows with reduced manual effort",
      },
    },

    mss: {
      icon: ShieldCheck,
      badge: "Managed Services",
      title: "Managed Services & Support",
      heroTitle: "Managed Enterprise Operations, Security & Support",
      heroSubtitle:
        "24/7 enterprise operations, monitoring, security, and support.",
      subtitle:
        "24/7 enterprise operations, monitoring, security, and support.",
      image: managedServicesImage,
      imageTitle: "24/7 Operations & Security",
      imageSubtitle:
        "Managed support, cybersecurity, SLA governance, and enterprise resilience.",
      description:
        "Our managed services model ensures your enterprise environment remains secure...",
      keyServices: [
        "24/7 NOC monitoring and incident response",
        "SLA-backed helpdesk support (L1/L2/L3)",
        "Managed cloud services",
        "Security patching & vulnerability management",
        "Performance optimization & capacity planning",
        "Disaster recovery planning and testing",
        "Compliance reporting",
        "Incident & SLA management",
      ],
      engagement: {
        type: "Monthly retainer with SLA guarantees",
        duration: "Ongoing managed engagement",
        deliverables:
          "Monthly reports, incident logs, patch summaries, DR test results",
        team: "Dedicated account manager, NOC engineers, security analysts",
        outcome:
          "Stable, secure, and continuously optimized enterprise environment",
      },
    },
  };

  const activeSolution = enterpriseSolutions[serviceId || "c&rs"] || {
    ...enterpriseSolutions["c&rs"],
    image: defaultEnterpriseImage,
  };

  return (
    <div className="min-h-screen bg-background">
      <Navigation />

      <div className="pt-20 pb-16">
        {/* Hero Section */}
        <div className="relative bg-gradient-to-r from-primary/10 via-secondary/5 to-background py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="space-y-4">
              <Badge className="bg-primary text-white hover:bg-primary/90">
                End-to-End Enterprise Solutions
              </Badge>

              <h1 className="text-4xl md:text-5xl font-bold text-foreground">
                {activeSolution.heroTitle}
              </h1>

              <p className="text-xl text-muted-foreground max-w-4xl">
                {activeSolution.heroSubtitle}
              </p>
            </div>
          </div>
        </div>

        {/* Enterprise Overview Image */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <Card className="overflow-hidden shadow-xl">
            <CardHeader>
              <CardTitle className="text-2xl">
                {activeSolution.imageTitle ||
                  "Enterprise Transformation Lifecycle"}
              </CardTitle>

              <p className="text-muted-foreground mt-2">
                {activeSolution.imageSubtitle ||
                  "Strategy, execution, integration, and operational excellence"}
              </p>
            </CardHeader>

            <CardContent>
              <div className="rounded-xl overflow-hidden border bg-black/5">
                <img
                  src={activeSolution.image || defaultEnterpriseImage}
                  alt={activeSolution.title}
                  className="w-full h-[300px] md:h-[600px] object-fit transition-transform duration-500 hover:scale-105"
                />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Active Solution Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="overflow-hidden">
            <CardHeader className="bg-gradient-to-r from-primary/5 via-secondary/5 to-background">
              <Badge className="w-fit mb-4">{activeSolution.badge}</Badge>

              <CardTitle className="text-3xl">{activeSolution.title}</CardTitle>

              <p className="text-lg text-muted-foreground">
                {activeSolution.subtitle}
              </p>

              <p className="text-muted-foreground mt-4">
                {activeSolution.description}
              </p>
            </CardHeader>

            <CardContent className="pt-8 space-y-10">
              {/* Key Services */}
              <div>
                <h3 className="text-2xl font-semibold mb-6">What We Offer</h3>

                <div className="grid md:grid-cols-2 gap-4">
                  {activeSolution.keyServices.map(
                    (service: string, idx: number) => (
                      <div key={idx} className="flex items-start">
                        <CheckCircle className={`h-5 w-5 ${idx % 2 === 0 ? 'text-primary shadow-glow' : 'text-secondary shadow-glow-secondary'} mr-3 mt-1 flex-shrink-0`} />
                        <span className="text-muted-foreground">{service}</span>
                      </div>
                    ),
                  )}
                </div>
              </div>

              {/* Engagement Details */}
              <div>
                <h3 className="text-2xl font-semibold mb-6">
                  Engagement Details
                </h3>

                <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-4">
                  {[
                    ["Engagement Type", activeSolution.engagement.type],
                    ["Duration", activeSolution.engagement.duration],
                    ["Deliverables", activeSolution.engagement.deliverables],
                    ["Team", activeSolution.engagement.team],
                    ["Outcome", activeSolution.engagement.outcome],
                  ].map(([title, value], idx) => (
                    <Card key={idx}>
                      <CardHeader>
                        <CardTitle className="text-base">{title}</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-sm text-muted-foreground">{value}</p>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* CTA */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <Card className="bg-gradient-to-r from-primary to-secondary text-white border-0 shadow-glow">
            <CardContent className="p-8 text-center">
              <h2 className="text-3xl font-bold mb-4">
                Ready to Transform Your Enterprise?
              </h2>

              <p className="text-xl mb-6 text-white/90">
                Build strategy, modernize systems, integrate operations, and
                scale with enterprise-grade support.
              </p>

              <GetStartedDialog />
            </CardContent>
          </Card>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default EndToEndEnterprise;
